import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Target,
  TrendingUp,
  Users,
  Calendar,
  Brain,
  BookOpen,
  ArrowRightLeft,
  CheckCircle2,
  BarChart3,
} from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from 'recharts';

interface FelixActionPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ActiveTab = 'esprit' | 'cadre' | 'maturite' | 'roadmap' | 'photo';

export default function FelixActionPlanModal({ isOpen, onClose }: FelixActionPlanModalProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('esprit');

  const dimensions = useMemo(
    () => [
      { key: 'marketing', label: 'Marketing WEB' },
      { key: 'crm', label: 'CRM / Tunnel de vente' },
      { key: 'soumissions', label: 'Soumissions & vitesse' },
      { key: 'documentation', label: 'Documentation PRIsME' },
      { key: 'agent', label: 'Agent OpenClaw' },
    ],
    []
  );

  // Échelle 0–5 (style ITIL / maturité). Avant = état initial; Après = cible.
  const beforeAfter = useMemo(
    () => ({
      before: {
        marketing: 1,
        crm: 2,
        soumissions: 2,
        documentation: 1,
        agent: 0,
      },
      after: {
        marketing: 4,
        crm: 5,
        soumissions: 4,
        documentation: 5,
        agent: 4,
      },
    }),
    []
  );

  const radarData = useMemo(() => {
    return dimensions.map((d) => ({
      dimension: d.label,
      before: beforeAfter.before[d.key as keyof typeof beforeAfter.before],
      after: beforeAfter.after[d.key as keyof typeof beforeAfter.after],
    }));
  }, [dimensions, beforeAfter]);

  const roadmapWeeks = useMemo(
    () => [
      {
        week: 'Semaine 1',
        focus: 'Mettre de l’ordre dans le tunnel actuel.',
        actions: [
          'Cartographier les étapes du tunnel actuel.',
          'Nommer les colonnes CRM et les critères de passage.',
          'Fixer la baseline des KPI de départ.',
          'Définir le rôle exact de l’agent OpenClaw dans le plan.',
        ],
        deliverable: 'Tunnel CRM en 1 page + baseline KPI + mandat de l’agent validé.',
        success: 'Le tunnel est compris, mesuré et le rôle du bot est cadré.',
      },
      {
        week: 'Semaine 2',
        focus: 'Clarifier le message commercial.',
        actions: [
          'Reformuler les messages marketing principaux.',
          'Définir les CTA web les plus simples.',
          'Uniformiser le pitch de vente et les angles.',
          'Lister les contenus que l’agent peut produire en draft.',
        ],
        deliverable: 'Message clair + 3 à 5 angles marketing + liste de drafts IA.',
        success: 'Le discours est simple, cohérent, répétable et déléguable au bot.',
      },
      {
        week: 'Semaine 3',
        focus: 'Faire du CRM une vraie machine de suivi.',
        actions: [
          'Définir les relances minimales obligatoires.',
          'Clarifier qui saisit quoi et quand dans le CRM.',
          'Rendre le pipeline lisible en temps réel.',
          'Installer OpenClaw dans un environnement contrôlé.',
        ],
        deliverable: 'CRM structuré + environnement OpenClaw prêt.',
        success: 'Le suivi devient constant et l’agent est techniquement en place.',
      },
      {
        week: 'Semaine 4',
        focus: 'Rendre les soumissions rapides et cohérentes.',
        actions: [
          'Créer 2 modèles de soumission standard.',
          'Clarifier les informations minimales requises.',
          'Réduire les frictions avant envoi.',
          'Brancher les skills nécessaires à l’agent (contenu, soumission, email).',
        ],
        deliverable: '2 gabarits de soumission + stack de skills branchée.',
        success: 'Les soumissions sortent plus vite et l’agent peut préparer 80 % du travail.',
      },
      {
        week: 'Semaine 5',
        focus: 'Augmenter la qualité des opportunités.',
        actions: [
          'Définir ce qu’est un lead qualifié.',
          'Améliorer la prise de besoin.',
          'Documenter les objections fréquentes et les réponses.',
          'Mettre en place le workflow d’approbation humaine pour le contenu sensible.',
        ],
        deliverable: 'Grille de qualification + objections/réponses + workflow d’approbation.',
        success: 'Le pipeline contient moins de bruit et l’agent reste cadré et sécuritaire.',
      },
      {
        week: 'Semaine 6',
        focus: 'Transformer le travail en actifs PRIsME.',
        actions: [
          'Documenter le tunnel, les KPI et les modèles de soumission.',
          'Tracer les décisions importantes.',
          'Rendre les preuves visibles dans PRIsME.',
          'Mesurer ce que l’agent exécute réellement chaque semaine.',
        ],
        deliverable: 'Cartes PRIsME à jour + preuves visibles + suivi d’adoption de l’agent.',
        success: 'Ce qui est amélioré est documenté, réutilisable et mesuré.',
      },
      {
        week: 'Semaine 7',
        focus: 'Corriger ce qui ne convertit pas assez.',
        actions: [
          'Relire les KPI et les écarts.',
          'Ajuster les séquences de suivi.',
          'Renforcer ce qui génère les meilleurs signaux.',
          'Optimiser les tâches confiées à l’agent.',
        ],
        deliverable: 'Plan d’ajustement appliqué au tunnel et au bot.',
        success: 'La conversion progresse et l’agent devient plus utile, pas juste plus actif.',
      },
      {
        week: 'Semaine 8',
        focus: 'Boucler, démontrer et préparer la suite.',
        actions: [
          'Comparer la baseline avec les résultats réels.',
          'Présenter le avant / après.',
          'Valider les prochaines priorités avec Martin.',
          'Décider quelles tâches restent humaines et lesquelles passent au bot.',
        ],
        deliverable: 'Synthèse exécutive + photo avant/après + plan agent version 2.',
        success: 'La progression est démontrée et le rôle durable de l’agent est clair.',
      },
    ],
    []
  );

  const targetKpis = useMemo(
    () => [
      { label: 'Leads qualifiés', value: '10+' },
      { label: 'Soumissions envoyées', value: '5+' },
      { label: 'Valeur du pipeline', value: '60' },
      { label: 'Taux de conversion', value: '40%' },
      { label: 'Tâches exécutées par agent', value: '80%' },
    ],
    []
  );

  const martinWeeklyChecks = useMemo(
    () => [
      'Le focus de la semaine est-il clair et respecté?',
      'Les actions prévues ont-elles réellement été exécutées?',
      'Le livrable attendu est-il visible et utilisable?',
      'Le bot exécute-t-il ce qui lui est confié dans le bon cadre?',
      'Les KPI montrent-ils une progression ou un blocage clair?',
      'Y a-t-il une décision à arbitrer tout de suite?',
    ],
    []
  );

  const deliveryRisks = useMemo(
    () => [
      'Le CRM reste un outil flou au lieu d’être une vraie machine commerciale.',
      'Les soumissions continuent de sortir lentement ou de façon inégale.',
      'Le pipeline grossit sans améliorer la qualité réelle des opportunités.',
      'Le bot agit sans cadre clair ou, à l’inverse, n’est pas assez utilisé pour créer un vrai gain.',
      'Le travail se fait, mais rien n’est prouvé, documenté ou transférable dans PRIsME.',
    ],
    []
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-70 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 16 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 16 }}
          transition={{ type: 'spring', stiffness: 220, damping: 22 }}
          className="relative w-[95vw] max-w-6xl max-h-[92vh] bg-slate-950 rounded-2xl shadow-2xl overflow-hidden border border-orange-400/30 flex flex-col"
        >
          <div className="p-6 border-b border-orange-400/30 bg-slate-950/90 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-linear-to-br from-orange-500 to-yellow-500 flex items-center justify-center shadow-lg shrink-0">
                <Target className="w-6 h-6 text-white" />
              </div>
              <img
                src="/FB.png"
                alt="Félix Bourassa"
                className="w-14 h-14 rounded-full object-cover border-2 border-orange-400 shadow-lg shrink-0"
              />
              <div>
                <p className="text-[0.7rem] uppercase tracking-wide text-orange-200 font-semibold">Plan d'action</p>
                <h2 className="text-lg md:text-xl font-bold text-white">Felix Avril–mai 2026</h2>
                <p className="text-xs text-slate-300/80 mt-1">
                  Cadre PRIsME pour gouverner l’exécution commerciale (maturité 0–5).
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="prisme-content-scroll flex-1 min-h-0 overflow-y-auto p-4 md:p-6 bg-slate-950/95 overscroll-contain">
            <div className="mb-6">
              <div className="flex flex-wrap gap-2 bg-white/5 rounded-xl p-2 border border-white/10">
                {(
                  [
                    ['esprit', 'Esprit'],
                    ['cadre', 'Cadre'],
                    ['maturite', 'Spider'],
                    ['roadmap', 'Roadmap'],
                    ['photo', 'Photo Avant/Après'],
                  ] as Array<[ActiveTab, string]>
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveTab(key)}
                    className={[
                      'px-3 py-2 rounded-lg text-xs font-semibold transition',
                      activeTab === key ? 'bg-orange-500 text-white' : 'text-slate-200 hover:bg-white/10',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'esprit' && (
              <section className="space-y-6">
                <div className="bg-linear-to-br from-orange-500/10 to-amber-500/10 border border-orange-400/20 rounded-xl p-5">
                  <h3 className="text-xl font-bold text-orange-200 flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    Mandat exécutif
                  </h3>
                  <p className="text-slate-100/90 mt-3 text-sm leading-relaxed">
                    Objectif : en 8 semaines, Felix doit structurer le marketing, le CRM et les soumissions de BZ Telecom
                    pour augmenter les opportunités qualifiées, accélérer le suivi et rendre la progression visible dans PRIsME.
                    Martin valide les arbitrages; Felix exécute.
                  </p>

                  <div className="mt-4 rounded-xl border border-orange-300/20 bg-slate-950/40 p-4">
                    <div className="text-[0.7rem] uppercase tracking-wide text-orange-200 font-semibold mb-2">Résumé exécutif</div>
                    <ul className="space-y-2 text-sm text-slate-100/90">
                      <li>• Felix doit rendre le tunnel commercial clair, mesuré et pilotable chaque semaine.</li>
                      <li>• Il doit faire sortir les soumissions plus vite, avec plus de constance et moins de friction.</li>
                      <li>• Il doit implanter un agent OpenClaw capable d’exécuter 80 % des tâches marketing et ventes répétitives.</li>
                      <li>• Il doit transformer les actions commerciales en preuves visibles dans PRIsME.</li>
                    </ul>
                  </div>

                  <div className="mt-4 rounded-xl border border-emerald-300/20 bg-emerald-500/10 p-4">
                    <div className="text-[0.7rem] uppercase tracking-wide text-emerald-200 font-semibold mb-2">Succès à 60 jours</div>
                    <ul className="space-y-2 text-sm text-slate-100/90">
                      <li>• Le tunnel CRM est compris, utilisé et suivi sans ambiguïté.</li>
                      <li>• Les soumissions sortent vite et avec une qualité plus constante.</li>
                      <li>• L’agent OpenClaw exécute 80 % des tâches prévues dans un cadre contrôlé.</li>
                      <li>• Les KPI montrent une progression réelle du pipeline et de la conversion.</li>
                      <li>• Les gains sont visibles, documentés et réutilisables dans PRIsME.</li>
                    </ul>
                  </div>

                  <div className="mt-4 grid sm:grid-cols-2 xl:grid-cols-5 gap-3">
                    {targetKpis.map((k) => (
                      <div key={k.label} className="bg-white/5 border border-white/10 rounded-lg p-4">
                        <div className="text-[0.7rem] uppercase tracking-wide text-slate-300">{k.label}</div>
                        <div className="text-2xl font-bold text-white mt-1">{k.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="text-xl font-bold text-cyan-200 flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5" />
                    Ce qu’on veut voir chaque semaine
                  </h3>
                  <ul className="mt-3 space-y-2 text-sm text-slate-200">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                      <span>Un focus principal simple, compris et assumé.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                      <span>Des actions concrètes que Felix peut exécuter sans ambiguïté.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                      <span>Un livrable visible et une mesure simple pour prouver l’avancement.</span>
                    </li>
                  </ul>
                </div>
              </section>
            )}

            {activeTab === 'cadre' && (
              <section className="space-y-6">
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-xl font-bold text-cyan-200 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Piliers (5)
                    </h3>
                    <div className="mt-3 space-y-3">
                      {dimensions.map((d) => (
                        <div key={d.key} className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-orange-500/20 border border-orange-400/30 flex items-center justify-center shrink-0">
                            <BarChart3 className="w-4 h-4 text-orange-200" />
                          </div>
                          <div>
                            <div className="font-semibold text-white text-sm">{d.label}</div>
                            <div className="text-xs text-slate-300/90 mt-1">
                              {d.key === 'marketing' && 'Positionnement, messages, angles et CTA qui génèrent des opportunités.'}
                              {d.key === 'crm' && 'Tunnel de vente lisible : étapes, critères, relances et visibilité pipeline.'}
                              {d.key === 'soumissions' && 'Soumissions plus rapides, plus claires et plus cohérentes.'}
                              {d.key === 'documentation' && 'PRIsME comme instrument : cartes, preuves, décisions et KPIs.'}
                              {d.key === 'agent' && 'OpenClaw comme moteur d’exécution sécurisé : drafts, suivis, rapports et soumissions sous approbation humaine.'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-linear-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/20 rounded-xl p-5">
                    <h3 className="text-xl font-bold text-cyan-200 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Gouvernance PRIsME (boundary)
                    </h3>
                    <p className="text-sm text-slate-100/90 mt-3 leading-relaxed">
                      Cadre PRIsME : décisions, cadence, mesures et artefacts documentés. On évite les procédures
                      opérationnelles (TactiCE) et on privilégie les preuves et l’adoption mesurée du tunnel CRM.
                    </p>
                    <div className="mt-4 space-y-2 text-sm text-slate-200">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                        <span>Martin = validation des arbitrages; Felix = exécution gouvernée.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                        <span>Mode continu : documentation en révision puis intégration après validation.</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-5">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-xl font-bold text-orange-200 flex items-center gap-2">
                      <BookOpen className="w-5 h-5" />
                      Livrables PRIsME attendus
                    </h3>
                    <ul className="mt-3 space-y-2 text-sm text-slate-200">
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                        <span>RACI “tunnel des ventes” (décider / exécuter / valider).</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                        <span>Table KPIs + photo Avant/Après (maturité 0–5 et performance).</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                        <span>Cartes PRIsME : Marketing WEB, CRM tunnel, Soumissions, Documentation.</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-linear-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/20 rounded-xl p-5">
                    <h3 className="text-xl font-bold text-cyan-200 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Contrôle hebdo Martin
                    </h3>
                    <ul className="mt-3 space-y-2 text-sm text-slate-200">
                      {martinWeeklyChecks.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-linear-to-br from-rose-500/10 to-red-500/10 border border-rose-400/20 rounded-xl p-5">
                  <h3 className="text-xl font-bold text-rose-200 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Risques si Felix ne livre pas
                  </h3>
                  <ul className="mt-3 space-y-2 text-sm text-slate-200">
                    {deliveryRisks.map((risk) => (
                      <li key={risk} className="flex items-start gap-3">
                        <span className="text-rose-300 mt-0.5">•</span>
                        <span>{risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            {activeTab === 'maturite' && (
              <section className="space-y-6">
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-cyan-200" />
                    Spider de maturité (Avant/Après)
                  </h3>
                  <p className="text-xs text-slate-300/90 mt-2">
                    Avant = état initial. Après = cible pour la fin de la période.
                  </p>

                  <div className="mt-4" style={{ width: '100%', height: 320 }}>
                    <ResponsiveContainer width="100%" height={320}>
                      <RadarChart data={radarData} margin={{ top: 10, right: 40, bottom: 10, left: 40 }}>
                        <PolarGrid stroke="rgba(148, 163, 184, 0.35)" />
                        <PolarAngleAxis dataKey="dimension" tick={{ fill: 'rgba(226,232,240,0.95)', fontSize: 12 }} />
                        <PolarRadiusAxis domain={[0, 5]} tickCount={6} stroke="rgba(226,232,240,0.35)" />
                        <Legend />
                        <Radar name="Avant" dataKey="before" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} strokeWidth={2} />
                        <Radar name="Après" dataKey="after" stroke="#10b981" fill="#10b981" fillOpacity={0.15} strokeWidth={2} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    {dimensions.map((d) => {
                      const b = beforeAfter.before[d.key as keyof typeof beforeAfter.before];
                      const a = beforeAfter.after[d.key as keyof typeof beforeAfter.after];
                      return (
                        <div key={d.key} className="bg-white/5 border border-white/10 rounded-lg p-4">
                          <div className="text-sm font-semibold text-white">{d.label}</div>
                          <div className="text-xs text-slate-300/90 mt-1">
                            Avant : <span className="text-orange-200 font-semibold">{b}</span> / 5
                            {'  '}|{'  '}
                            Après : <span className="text-emerald-300 font-semibold">{a}</span> / 5
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'roadmap' && (
              <section className="space-y-6">
                <div className="bg-linear-to-br from-orange-500/10 to-yellow-500/10 border border-orange-400/20 rounded-xl p-5">
                  <h3 className="text-xl font-bold text-orange-200 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Roadmap (8 semaines, Avril–mai 2026)
                  </h3>
                  <p className="text-sm text-slate-100/90 mt-2 leading-relaxed">
                    Lecture exécutive : chaque semaine doit préciser un focus, des actions simples, un livrable et une mesure de succès.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {roadmapWeeks.map((w) => (
                    <div key={w.week} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4">
                      <div>
                        <div className="text-sm font-semibold text-white">{w.week}</div>
                        <div className="text-xs text-orange-200 mt-1">{w.focus}</div>
                      </div>

                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Actions concrètes</div>
                        <ul className="space-y-2 text-xs text-slate-200">
                          {w.actions.map((f) => (
                            <li key={f} className="flex items-start gap-2">
                              <span className="text-orange-300 mt-0.5">•</span>
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="grid gap-3">
                        <div className="rounded-lg border border-cyan-400/20 bg-cyan-500/10 p-3">
                          <div className="text-[11px] uppercase tracking-wide text-cyan-200 mb-1">Livrable attendu</div>
                          <div className="text-xs text-slate-100">{w.deliverable}</div>
                        </div>
                        <div className="rounded-lg border border-emerald-400/20 bg-emerald-500/10 p-3">
                          <div className="text-[11px] uppercase tracking-wide text-emerald-200 mb-1">Mesure de succès</div>
                          <div className="text-xs text-slate-100">{w.success}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'photo' && (
              <section className="space-y-6">
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-300" />
                    Photo Avant / Après
                  </h3>
                  <p className="text-xs text-slate-300/90 mt-2">
                    Vue “instantanée” des scores de maturité (0–5) sur les 5 axes.
                  </p>

                  <div className="mt-4 grid lg:grid-cols-2 gap-4">
                    <div className="bg-slate-900/40 border border-white/10 rounded-xl p-4">
                      <div className="text-sm font-semibold text-orange-200 mb-2">Avant</div>
                      <div style={{ width: '100%', height: 280 }}>
                        <ResponsiveContainer width="100%" height={280}>
                          <RadarChart data={radarData} margin={{ top: 10, right: 40, bottom: 10, left: 40 }}>
                            <PolarGrid stroke="rgba(148, 163, 184, 0.35)" />
                            <PolarAngleAxis dataKey="dimension" tick={{ fill: 'rgba(226,232,240,0.95)', fontSize: 12 }} />
                            <PolarRadiusAxis domain={[0, 5]} tickCount={6} stroke="rgba(226,232,240,0.35)" />
                            <Legend />
                            <Radar
                              name="Avant"
                              dataKey="before"
                              stroke="#f59e0b"
                              fill="#f59e0b"
                              fillOpacity={0.18}
                              strokeWidth={2}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="bg-slate-900/40 border border-white/10 rounded-xl p-4">
                      <div className="text-sm font-semibold text-emerald-300 mb-2">Après</div>
                      <div style={{ width: '100%', height: 280 }}>
                        <ResponsiveContainer width="100%" height={280}>
                          <RadarChart data={radarData} margin={{ top: 10, right: 40, bottom: 10, left: 40 }}>
                            <PolarGrid stroke="rgba(148, 163, 184, 0.35)" />
                            <PolarAngleAxis dataKey="dimension" tick={{ fill: 'rgba(226,232,240,0.95)', fontSize: 12 }} />
                            <PolarRadiusAxis domain={[0, 5]} tickCount={6} stroke="rgba(226,232,240,0.35)" />
                            <Legend />
                            <Radar
                              name="Après"
                              dataKey="after"
                              stroke="#10b981"
                              fill="#10b981"
                              fillOpacity={0.18}
                              strokeWidth={2}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

